"use client"

import { useEffect, useState, useRef } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import AnimatedChatbotDemo from "@/components/AnimatedChatbotDemo"
import AIModelsCarousel from "@/components/AIModelsCarousel"
import { GalaxyStarsBackground } from "@/components/galaxy-stars-background"
import Image from "next/image"
import { useTranslation } from "@/hooks/useTranslation"
import { getMarketingDemoVideoUrl } from "@/lib/marketing-demo"
import { 
  MessageSquare, 
  Zap, 
  Shield, 
  Globe, 
  Sparkles, 
  CheckCircle2,
  ArrowLeft,
  BarChart3,
  Users,
  Clock,
  Check,
  X,
  HelpCircle,
  FileText,
  Menu
} from "lucide-react"
import { AnimatedCounter } from "@/components/animated-counter"
import { BlogAutoFetch } from "@/components/blog-auto-fetch"
import { HomeLanguageSwitcher } from "@/components/marketing/HomeLanguageSwitcher"
import "./home-premium.css"

interface PricingPlan {
  id: string
  name: string
  price: number
  period: string
  description: string
  popular: boolean
  customBadge?: string
  fixedPrice?: boolean
  features: Array<{
    name: string
    value: string | boolean
    tooltip: string
  }>
}

interface ConversationMessage {
  sender: "ai" | "user"
  text: string
  isPlaceholder?: boolean
}

interface ConversationData {
  name: string
  backgroundImage: string
  messages: ConversationMessage[]
}

// Persian-only marketing page (Iran licenses, local contact info)
const LOCALE = 'fa' as const

export default function HomePage() {
  const router = useRouter()
const { t, tArray, isRTL, numberLocale, isLoaded } = useTranslation(LOCALE)
  const demoVideoUrl = getMarketingDemoVideoUrl(LOCALE)
  const chatDirection = isRTL ? "rtl" : "ltr"
  const [isVisible, setIsVisible] = useState(false)
  const isWelcomeSoundDisabled = process.env.NEXT_PUBLIC_DISABLE_HOME_SOUNDS === "true"

  // Helper function to navigate based on login status
  const handleEnterClick = () => {
    if (isLoggedIn) {
      router.push('/pathway?window=user-dashboard')
    } else {
      router.push('/pathway')
    }
  }
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([])
  const [conversations, setConversations] = useState<ConversationData[]>([])
  const [isYearly, setIsYearly] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [headerVisible, setHeaderVisible] = useState(true)
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false)
  const [activeStep, setActiveStep] = useState<number>(0)
  const [stepVisibility, setStepVisibility] = useState<Record<number, boolean>>({1: false, 2: false, 3: false})
  const [hoveredPlan, setHoveredPlan] = useState<string | null>(null)
  const lastScrollY = useRef(0)
  const stepRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    setIsVisible(true)

    // Restore yearly selection (shared across pricing surfaces)
    try {
      const stored = localStorage.getItem('pricingYearly')
      if (stored === 'true') setIsYearly(true)
    } catch {}
    
    // Load pricing plans from locale-specific JSON
    fetch(`/pricing-plans.${LOCALE}.json`)
      .then(res => res.json())
      .then(data => setPricingPlans(data.plans))
      .catch(err => console.error('Failed to load pricing plans:', err))
    
    // Load chatbot demo conversations from translations (only when translations are loaded)
    if (isLoaded) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const convoData = tArray<any>('chatbotDemo.conversations')
      if (convoData.length > 0) {
        setConversations(convoData as ConversationData[])
      }
    }
    
    // Check login status
    const checkAuth = () => {
      const loginStatus = localStorage.getItem("isLoggedIn") === "true"
      setIsLoggedIn(loginStatus)
    }
    
    checkAuth()
    const handleStorageChange = () => checkAuth()
    window.addEventListener("storage", handleStorageChange)
    window.addEventListener("loginStatusChanged", handleStorageChange)

    // Smart header - show on scroll up, hide on scroll down
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setHeaderVisible(false) // Scrolling down
      } else {
        setHeaderVisible(true) // Scrolling up
      }
      lastScrollY.current = currentScrollY
    }
    window.addEventListener('scroll', handleScroll, { passive: true })

    // Intersection Observer for How It Works section scroll animations
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const stepAttr = entry.target.getAttribute('data-step')
        if (stepAttr) {
          const stepNum = parseInt(stepAttr, 10)
          if (entry.isIntersecting) {
            // Step is visible
            setStepVisibility(prev => ({
              ...prev,
              [stepNum]: true
            }))
            // Update active step
            setActiveStep(prev => Math.max(prev, stepNum))
          }
        }
      })
    }

    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '-20% 0px -30% 0px',
      threshold: 0.1
    })

    // Observe all step elements
    stepRefs.current.forEach((ref) => {
      if (ref) {
        observer.observe(ref)
      }
    })

    return () => {
      observer.disconnect()
      window.removeEventListener("storage", handleStorageChange)
      window.removeEventListener("loginStatusChanged", handleStorageChange)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [isLoaded])

  // Clear last card highlight once CTA appears (matches cards 1–2 losing line on next step)
  useEffect(() => {
    if (activeStep !== 3) return
    const timer = window.setTimeout(() => setActiveStep(4), 700)
    return () => clearTimeout(timer)
  }, [activeStep])

  useEffect(() => {
    if (!isLoaded) return

    const win = window as Window & { __ATIRAME_EMBED_LOADED__?: boolean }
    if (win.__ATIRAME_EMBED_LOADED__) return
    if (document.querySelector('script[src*="embed-multilang"]') || document.getElementById('chatbot-toggle')) {
      win.__ATIRAME_EMBED_LOADED__ = true
      return
    }

    win.__ATIRAME_EMBED_LOADED__ = true

    const script = document.createElement('script')
    script.src = '/dist2/embed-multilang.js'
    script.setAttribute('data-position', 'bottom-right')
    script.setAttribute('data-language', LOCALE)
    script.setAttribute('data-custom-title', t('chatbot.title'))
    script.setAttribute('data-custom-subtitle', t('chatbot.subtitle'))
    script.async = true
    script.defer = true
    document.body.appendChild(script)
  }, [isLoaded, t])

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen relative overflow-hidden">
      <BlogAutoFetch />
      {/* Background - fixed behind all content */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50"></div>
      </div>
      
      {/* Content wrapper with relative z-index */}
      <div className="relative z-10">
      {/* Navigation */}
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200/50 transition-transform duration-300 ${
          headerVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo - Always visible */}
            <div className="flex items-center gap-2">
              <Image src="/dist2/robot-icon.png" alt={t('nav.brand')} width={36} height={36} className="object-contain w-8 h-8 md:w-10 md:h-10" />
              <span className="text-xl md:text-2xl font-bold text-[#0d7ad9]">
                {t('nav.brand')}
              </span>
            </div>
            
            {/* Desktop Navigation - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-3">
              <HomeLanguageSwitcher />
              <Button
                variant="ghost" 
                className="text-gray-700 hover:text-white hover:bg-[#01ADEF] rounded-full px-5 py-2 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-105"
                onClick={() => {
                  const element = document.getElementById('features');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {t('nav.features')}
              </Button>
              <Button 
                variant="ghost" 
                className="text-gray-700 hover:text-white hover:bg-[#01ADEF] rounded-full px-5 py-2 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-105"
                onClick={() => {
                  const element = document.getElementById('pricing');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {t('nav.pricing')}
              </Button>
              <Button 
                variant="ghost" 
                className="text-gray-700 hover:text-white hover:bg-[#01ADEF] rounded-full px-5 py-2 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-105"
                onClick={() => {
                  const element = document.getElementById('plugin');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {t('nav.plugin')}
              </Button>
              <Button 
                variant="outline" 
                className="border-2 border-[#01ADEF] text-[#01ADEF] hover:bg-[#01ADEF] hover:text-white rounded-full px-5 py-2 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-105"
                onClick={handleEnterClick}
              >
                {t('nav.login')}
              </Button>
            </div>

            {/* Mobile Navigation - Login + More dropdown */}
            <div className="flex md:hidden items-center gap-2">
              <HomeLanguageSwitcher />
              {/* Login Button - Always visible on mobile */}
              <Button 
                variant="outline" 
                size="sm"
                className="border-2 border-[#01ADEF] text-[#01ADEF] hover:bg-[#01ADEF] hover:text-white rounded-full px-3 py-1.5 text-sm shadow-sm hover:shadow-lg transition-all duration-300"
                onClick={handleEnterClick}
              >
                {t('nav.loginMobile')}
              </Button>
              
              {/* More Dropdown - Hamburger Menu */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-700 hover:text-white hover:bg-[#01ADEF] rounded-full p-2 transition-all duration-300"
                  onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                >
                  <Menu className="w-5 h-5" />
                </Button>
                
                {/* Dropdown Menu */}
                {isMoreMenuOpen && (
                  <>
                    {/* Backdrop to close menu when clicking outside */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsMoreMenuOpen(false)}
                    />
                    {/* Menu Items */}
                    <div className="absolute left-0 top-full mt-2 w-40 bg-white/95 backdrop-blur-md rounded-xl shadow-xl border border-gray-200/50 overflow-hidden z-50">
                      <button
                        className="w-full text-right px-4 py-3 text-sm text-gray-700 hover:bg-[#01ADEF] hover:text-white transition-colors duration-200"
                        onClick={() => {
                          setIsMoreMenuOpen(false);
                          const element = document.getElementById('features');
                          element?.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        {t('nav.features')}
                      </button>
                      <button
                        className="w-full text-right px-4 py-3 text-sm text-gray-700 hover:bg-[#01ADEF] hover:text-white transition-colors duration-200"
                        onClick={() => {
                          setIsMoreMenuOpen(false);
                          const element = document.getElementById('pricing');
                          element?.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        {t('nav.pricing')}
                      </button>
                      <button
                        className="w-full text-right px-4 py-3 text-sm text-gray-700 hover:bg-[#01ADEF] hover:text-white transition-colors duration-200"
                        onClick={() => {
                          setIsMoreMenuOpen(false);
                          const element = document.getElementById('plugin');
                          element?.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        {t('nav.plugin')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="home-premium relative overflow-hidden pt-[7.5rem] pb-20 md:pb-28">
        {/* Background decoration - a single soft wash behind the headline side */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
          <div className="absolute -top-56 right-[-10rem] w-[36rem] h-[36rem] bg-[var(--hp-brand)]/[0.07] rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
            {/* Left: Text Content */}
            <div className={`hp-reveal ${isVisible ? 'is-in' : ''}`}>
              <span className="hp-eyebrow mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                {t('nav.brand')}
              </span>

              <h1 className="hp-display mb-6">
                {t('hero.title')}{" "}
                <span className="text-[var(--hp-brand)]">
                  {t('hero.titleHighlight')}
                </span>
              </h1>

              <p className="hp-lede max-w-xl mb-9">
                {t('hero.subtitle')}
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  size="lg"
                  className="hp-btn hp-btn--primary"
                  onClick={handleEnterClick}
                >
                  {t('hero.tryButton')}
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                {demoVideoUrl && (
                  <a
                    href={demoVideoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      size="lg"
                      variant="outline"
                      className="hp-btn hp-btn--secondary"
                    >
                      {t('hero.demoButton')}
                    </Button>
                  </a>
                )}
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap gap-x-7 gap-y-3 mt-10">
                {[t('hero.trust1'), t('hero.trust2'), t('hero.trust3')].map((claim) => (
                  <div key={claim} className="flex items-center gap-2">
                    <CheckCircle2 className="hp-tick w-4 h-4" />
                    <span className="text-sm font-medium text-[var(--hp-body)]">{claim}</span>
                  </div>
                ))}
              </div>

              <hr className="hp-divider my-8 max-w-xl" />

              {/* Description text */}
              <p className="hp-body max-w-xl">
                {t('hero.description')}
              </p>
            </div>

            {/* Right: Animated Chatbot Demo */}
            <div
              className={`hp-reveal relative h-full min-h-[560px] lg:min-h-[660px] rounded-[var(--hp-radius-lg)] ${isVisible ? 'is-in' : ''}`}
              style={{ transitionDelay: '160ms' }}
            >
              {conversations.length > 0 && (
                <AnimatedChatbotDemo 
                  className="w-full h-full"
                  conversations={conversations}
                  headerTitle={t('chatbotDemo.headerTitle')}
                  inputPlaceholder={t('chatbotDemo.inputPlaceholder')}
                  brandName={t('chatbotDemo.brandName')}
                  todayLabel={t('chatbotDemo.today')}
                  direction={chatDirection}
                />
              )}
            </div>
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section className="py-16 relative bg-[#08075C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { image: "/images/customers.png", end: 100, suffix: "+", label: t('stats.customers') },
              { image: "/images/message-analyze.png", end: 50, suffix: "K+", label: t('stats.messages') },
              { image: "/images/increasing-chart.png", end: 30, suffix: LOCALE === 'fa' ? "٪+" : "%+", label: t('stats.salesIncrease') },
              { image: "/images/24_hours.png", end: 24, suffix: LOCALE === 'fa' ? "/۷" : "/7", label: t('stats.support') },
            ].map((stat, index) => (
              <div 
                key={index}
                className="group text-center p-6 rounded-2xl bg-white border border-gray-200 hover:shadow-xl transition-all duration-500 hover:-translate-y-2 relative overflow-hidden"
              >
                {/* Animated background gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#01ADEF]/5 to-[#08075C]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative z-10">
                  <div className="mb-4 transform group-hover:scale-110 transition-transform duration-500">
                    {stat.image && (
                      <Image 
                        src={stat.image} 
                        alt={stat.label}
                        width={40}
                        height={40}
                        className="w-10 h-10 object-contain mx-auto"
                      />
                    )}
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 group-hover:text-[#01ADEF] transition-colors duration-300">
                    <AnimatedCounter end={stat.end} suffix={stat.suffix} duration={2000} locale={LOCALE} />
                  </div>
                  <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LLM Companies Slider - Enhanced Design */}
      <section className="py-12 relative overflow-hidden">
        <GalaxyStarsBackground variant="section" desktopOnlyStars />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            {/* White box behind header text with bold border/outline shadow */}
            <div 
              className="inline-block bg-white rounded-2xl px-6 py-4 mb-3"
              style={{
                boxShadow: `
                  0 0 0 3px rgba(8, 7, 92, 0.3),
                  0 0 0 6px rgba(1, 173, 239, 0.2),
                  0 10px 40px rgba(0, 0, 0, 0.25),
                  0 5px 20px rgba(0, 0, 0, 0.15)
                `,
                border: '2px solid rgba(8, 7, 92, 0.4)',
              }}
            >
              <h2 className="text-xl md:text-2xl lg:text-3xl font-extrabold text-[#0c5492] mb-3">
                {t('aiModels.title')}{" "}
                <span className="text-[#0c5492]">
                  {t('aiModels.titleHighlight')}
                </span>
              </h2>
              <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
                {t('aiModels.subtitle')}
              </p>
            </div>
          </div>

          {/* 3D Circular Carousel */}
          <AIModelsCarousel />
        </div>
      </section>

      {/* Features list below slider - separate section */}
      <section className="py-12 relative bg-[#08075C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-4">
            {tArray<{title: string; description: string}>('featuresGrid').map((item, idx) => (
              <div key={idx} className="group flex flex-col items-center text-center p-4 rounded-2xl bg-white border border-gray-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="w-10 h-10 mb-3 group-hover:scale-110 transition-transform duration-300">
                  <Image 
                    src={["/images/speed.png", "/images/goal.png", "/images/Global.png"][idx]} 
                    alt={item.title}
                    width={40}
                    height={40}
                    className="object-contain w-10 h-10"
                  />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('whyAtiram.title')}
            </h2>
            <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
              {t('whyAtiram.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {tArray<{title: string; description: string}>('whyAtiram.cards').map((feature, index) => (
              <div
                key={index}
                className="group overflow-hidden rounded-2xl bg-white border border-gray-200 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 text-right relative"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={["/images/Zap.jpg", "/images/Sparkles.jpg", "/images/FileText.png", "/images/Clock.png", "/images/MessageSquare.png", "/images/BarChart3.png"][index]} 
                    alt={feature.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.currentTarget.src = `https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop`
                    }}
                  />
                  {/* Title overlay on image - visible by default */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                    <h3 className="text-white font-bold text-lg leading-tight">{feature.title}</h3>
                  </div>
                  {/* Description overlay - appears on hover */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur-sm transform translate-y-full group-hover:translate-y-0 transition-all duration-300 ease-out">
                    <p className="text-gray-700 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section - Vertical Timeline */}
      <section className="py-24 relative overflow-hidden" id="how-it-works">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <Image
            src="/images/how-dose-work.png"
            alt="Background"
            fill
            className="object-cover"
            priority
            quality={90}
          />
          {/* Gradient Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-blue-50/95"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('howItWorks.title')}
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto">
              {t('howItWorks.subtitle')}
            </p>
          </div>

          {/* Vertical Timeline Container */}
          <div className="relative">
            {/* Vertical Progress Line - Left Side */}
            <div className="absolute right-8 md:right-1/2 top-0 bottom-0 w-1 bg-gray-200 rounded-full transform md:translate-x-1/2">
              {/* Animated Fill */}
              <div 
                className="w-full bg-gradient-to-b from-[#01ADEF] to-[#08075C] rounded-full transition-all duration-1000 ease-out"
                style={{ height: `${(Math.min(activeStep, 3) / 3) * 100}%` }}
              ></div>
            </div>

            {/* Steps */}
            <div className="space-y-12 md:space-y-24">
              {(() => {
                const stepsData = tArray<{title: string; description: string; details: string; time: string; features: string[]}>('howItWorks.steps')
                const stepImages = ["/images/registration.png", "/images/settings.png", "/images/quality_warranty.png"]
                
                return stepsData.map((item, index) => {
                  const stepNum = index + 1
                  const isVisible = stepVisibility[stepNum]
                  const isActive = activeStep >= stepNum
                  const ImageSource = stepImages[index] || stepImages[0]
                  const isEven = index % 2 === 0
                
                  return (
                  <div
                    key={index} 
                    ref={el => { stepRefs.current[index] = el }}
                    data-step={stepNum}
                    className={`relative flex items-center ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} transition-all duration-700 ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                  >
                    {/* Step Circle on Timeline */}
                    <div className="absolute right-8 md:right-1/2 transform md:translate-x-1/2 z-20">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold transition-all duration-500 ${
                        isActive 
                          ? 'bg-[#0d7ad9] text-white shadow-lg shadow-[#01ADEF]/30 scale-110' 
                          : 'bg-white border-4 border-gray-200 text-gray-400'
                      }`}>
                        {isActive ? (
                          <AnimatedCounter end={stepNum} duration={800} locale={LOCALE} />
                        ) : (
                          stepNum
                        )}
                        {/* Pulse effect for active step */}
                        {isActive && activeStep === stepNum && (
                          <span className="absolute inset-0 rounded-full bg-[#01ADEF] animate-ping opacity-20"></span>
                        )}
                      </div>
                    </div>

                    {/* Card */}
                    <div className={`w-full md:w-5/12 ${isEven ? 'md:pl-16 md:pr-8' : 'md:pr-16 md:pl-8'} pr-20 md:pr-auto`}>
                      <div className={`bg-white rounded-3xl p-6 shadow-lg border-2 transition-all duration-500 cursor-pointer ${
                        activeStep === stepNum 
                          ? 'border-[#01ADEF] shadow-xl shadow-[#01ADEF]/10 scale-105' 
                          : 'border-gray-100 hover:border-[#01ADEF]/50 hover:shadow-xl'
                      }`}>
                        {/* Time indicator */}
                        <div className={`flex items-center gap-2 mb-4 text-sm font-medium transition-colors duration-300 ${
                          isActive ? 'text-[#01ADEF]' : 'text-gray-400'
                        }`}>
                          <Clock className="w-4 h-4" />
                          <span>{item.time}</span>
                        </div>

                        {/* Icon or Image */}
                        <div className={`mb-4 transition-all duration-500 ${activeStep === stepNum ? 'scale-110' : ''}`}>
                          <Image 
                            src={ImageSource} 
                            alt={item.title}
                            width={40}
                            height={40}
                            className="object-contain w-10 h-10"
                          />
                        </div>

                        {/* Title */}
                        <h3 className={`text-lg font-bold mb-2 transition-colors duration-300 ${
                          isActive ? 'text-[#08075C]' : 'text-gray-900'
                        }`}>
                          {item.title}
                        </h3>

                        {/* Description */}
                        <p className="text-gray-600 mb-3 text-sm">{item.description}</p>

                        {/* Expanded Details - visible when step is active or visible */}
                        <div className={`overflow-hidden transition-all duration-500 ${
                          isVisible ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
                        }`}>
                          <p className="text-gray-700 text-xs leading-relaxed mb-3 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-3">
                            {item.details}
                          </p>
                          
                          {/* Feature tags */}
                          <div className="flex flex-wrap gap-2">
                            {item.features.map((feature, idx) => (
                              <span 
                                key={idx}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-[#01ADEF]/10 to-[#08075C]/10 text-[#08075C] text-xs rounded-full"
                              >
                                <CheckCircle2 className="w-3 h-3 text-green-500" />
                                {feature}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Progress indicator line at bottom */}
                        <div className={`absolute bottom-0 left-0 h-1 bg-[#0d7ad9] transition-all duration-700 rounded-b-3xl ${
                          activeStep === stepNum ? 'w-full' : 'w-0'
                        }`}></div>
                      </div>
                    </div>

                    {/* Empty space for alternating layout */}
                    <div className="hidden md:block w-5/12"></div>
                  </div>
                  )
                })
              })()}
            </div>
          </div>

          {/* CTA Button */}
          <div className={`text-center mt-16 transition-all duration-700 ${
            activeStep >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <Button 
              size="lg"
              className="bg-[#0d7ad9] text-white hover:opacity-90 px-10 py-7 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-lg"
              onClick={handleEnterClick}
            >
              {t('howItWorks.ctaButton')}
              <ArrowLeft className="mr-2 w-5 h-5" />
            </Button>
            <p className="text-gray-500 text-sm mt-4">
              {t('howItWorks.ctaSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* WordPress Plugin Section */}
      <section id="plugin" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-4">
                {t('wordpress.title')}
              </h2>
              <p className="text-right text-gray-600 mt-3 max-w-xl mx-auto mb-8 leading-relaxed text-lg">
                {t('wordpress.description')}
              </p>
              <div className="space-y-3">
                {tArray<string>('wordpress.benefits').map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Button 
                  size="lg"
                  className="bg-[#0d7ad9] text-white hover:opacity-90 px-8 py-6 rounded-full"
                  onClick={() => router.push('/pathway?window=features')}
                >
                  {t('wordpress.downloadButton')}
                  <ArrowLeft className="mr-2 w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <Image
                  src="/images/wordpress-screen.png"
                  alt="افزونه وردپرس آتیرام"
                  width={800}
                  height={600}
                  className="w-full h-auto object-contain rounded-2xl"
                  priority
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-4">
              {t('pricing.title')}
            </h2>
            <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto mb-6">
              {t('pricing.subtitle')}
            </p>
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-4">
                <span className={`font-semibold transition-all duration-300 ${!isYearly ? 'text-[#01ADEF] font-bold text-lg' : 'text-gray-500 text-sm'}`}>{t('pricing.monthly')}</span>
                <button
                  onClick={() => {
                    const next = !isYearly
                    setIsYearly(next)
                    try {
                      localStorage.setItem('pricingYearly', next ? 'true' : 'false')
                    } catch {}
                  }}
                  className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${isYearly ? 'bg-[#01ADEF]' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-1 right-1 w-5 h-5 bg-white rounded-full transition-transform duration-300 ${isYearly ? '-translate-x-7' : 'translate-x-0'}`}></div>
                </button>
                <span className={`font-semibold transition-all duration-300 ${isYearly ? 'text-[#01ADEF] font-bold text-lg' : 'text-gray-500 text-sm'}`}>{t('pricing.yearly')}</span>
              </div>
              {isYearly && (
                <span className="text-sm text-green-600 font-semibold bg-green-100 px-3 py-1 rounded-full">
                  {t('pricing.yearlyDiscount')}
                </span>
              )}
            </div>
          </div>

          {pricingPlans.length > 0 && (
            <>
              {/* First 4 plans in a row */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                {pricingPlans.slice(0, 4).map((plan) => {
                  const displayPrice = isYearly && !plan.fixedPrice ? Math.round(plan.price * 9) : plan.price
                  return (
                    <div
                      key={plan.id}
                      onMouseEnter={() => setHoveredPlan(plan.id)}
                      onMouseLeave={() => setHoveredPlan(null)}
                      className={`relative bg-white rounded-3xl border-2 p-8 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 ${
                        hoveredPlan === plan.id
                          ? 'border-[#01ADEF] shadow-xl scale-105 ring-2 ring-[#01ADEF]/20'
                          : 'border-gray-200 shadow-md'
                      }`}
                      style={{
                        boxShadow: hoveredPlan === plan.id
                          ? '0 20px 25px -5px rgba(1, 173, 239, 0.1), 0 10px 10px -5px rgba(1, 173, 239, 0.04)'
                          : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                      }}
                    >
                      {plan.popular && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#0d7ad9] text-white px-4 py-1 rounded-full text-sm font-semibold">
                          {t('pricing.popular')}
                        </div>
                      )}
                      {plan.customBadge && !plan.popular && (
                        <div 
                          className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-white px-4 py-1 rounded-full text-sm font-semibold"
                          style={{ backgroundColor: '#08a463' }}
                        >
                          {plan.customBadge}
                        </div>
                      )}
                      <div className="text-center mb-6">
                        <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2">{plan.name}</h3>
                        <div className="mb-2">
                          <span className="text-2xl md:text-3xl font-extrabold text-gray-900">
                            {plan.price === 0 ? t('pricing.free') : `${displayPrice.toLocaleString(numberLocale)}`}
                          </span>
                          {plan.price > 0 && (
                            <span className="text-gray-600 text-sm md:text-base mr-1">{t('pricing.currency')}</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{plan.period}</p>
                        <p className="text-xs text-gray-600 mt-1">{plan.description}</p>
                      </div>
                      <div className="mb-6">
                        <Button
                          className={`w-full rounded-2xl py-4 text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                            hoveredPlan === plan.id
                              ? 'bg-[#0d7ad9] text-white hover:opacity-90 shadow-lg hover:shadow-2xl'
                              : 'bg-gray-100 text-gray-900 hover:bg-[#01ADEF] hover:text-white shadow-md hover:shadow-xl'
                          }`}
                          onClick={() => {
                            // Store selected plan info
                            const planInfo = {
                              planId: plan.id,
                              planName: plan.name,
                              amount: isYearly && !plan.fixedPrice ? Math.round(plan.price * 9) : plan.price,
                              billingPeriod: isYearly ? 'yearly' : 'monthly'
                            }
                            localStorage.setItem('selectedPlan', JSON.stringify(planInfo))
                            
                            // Check login status
                            const loginStatus = localStorage.getItem("isLoggedIn") === "true"
                            
                            if (loginStatus) {
                              // User is logged in → go to payment window
                              router.push(`/pathway?window=payment&planId=${plan.id}`)
                            } else {
                              // User is not logged in → go to mobile-auth window first
                              // Store redirect info so after login, user goes to payment
                              localStorage.setItem('redirectAfterLogin', JSON.stringify({
                                window: 'payment',
                                planId: plan.id
                              }))
                              router.push('/pathway?window=mobile-auth')
                            }
                          }}
                        >
                          {plan.price === 0 ? t('pricing.startFree') : t('pricing.getStart')}
                          <ArrowLeft className="mr-2 w-3 h-3" />
                        </Button>
                      </div>
                      <div className="space-y-2.5">
                        {plan.features.slice(0, 5).map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-1.5">
                            {typeof feature.value === 'boolean' ? (
                              feature.value ? (
                                <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                              ) : (
                                <X className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
                              )
                            ) : (
                              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1 flex flex-col">
                              <span className="text-xs text-gray-700">{feature.name}</span>
                              {typeof feature.value === 'string' && (
                                <span className="text-xs font-semibold text-[#01ADEF]">
                                  {feature.value}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
              
              {/* Economic yearly plan - centered below */}
              {pricingPlans.length > 4 && (
                <div className="flex justify-center mb-12">
                  {(() => {
                    const plan = pricingPlans[4]
                    const displayPrice = plan.price
                    return (
                      <div
                        onMouseEnter={() => setHoveredPlan(plan.id)}
                        onMouseLeave={() => setHoveredPlan(null)}
                        className={`relative bg-white rounded-3xl border-2 p-8 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 w-full max-w-sm ${
                          hoveredPlan === plan.id
                            ? 'border-[#08a463] shadow-xl scale-105 ring-2 ring-[#08a463]/20'
                            : 'border-gray-200 shadow-md'
                        }`}
                        style={{
                          boxShadow: hoveredPlan === plan.id
                            ? '0 20px 25px -5px rgba(8, 164, 99, 0.1), 0 10px 10px -5px rgba(8, 164, 99, 0.04)'
                            : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                        }}
                      >
                        {plan.customBadge && (
                          <div 
                            className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-white px-4 py-1 rounded-full text-sm font-semibold"
                            style={{ backgroundColor: '#08a463' }}
                          >
                            {plan.customBadge}
                          </div>
                        )}
                        <div className="text-center mb-6">
                          <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2">{plan.name}</h3>
                          <div className="mb-2">
                            <span className="text-2xl md:text-3xl font-extrabold text-gray-900">
                              {displayPrice.toLocaleString(numberLocale)}
                            </span>
                            <span className="text-gray-600 text-sm md:text-base mr-1">{t('pricing.currency')}</span>
                          </div>
                          <p className="text-xs text-gray-500">{plan.period}</p>
                          <p className="text-xs text-gray-600 mt-1">{plan.description}</p>
                        </div>
                        <div className="mb-6">
                          <Button
                            className={`w-full rounded-2xl py-4 text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                              hoveredPlan === plan.id
                                ? 'text-white shadow-lg hover:shadow-2xl'
                                : 'bg-gray-100 text-gray-900 hover:bg-[#08a463] hover:text-white shadow-md hover:shadow-xl'
                            }`}
                            style={hoveredPlan === plan.id ? { backgroundColor: '#08a463' } : {}}
                            onClick={() => {
                              // Store selected plan info
                              const planInfo = {
                                planId: plan.id,
                                planName: plan.name,
                                amount: plan.price,
                                billingPeriod: 'yearly'
                              }
                              localStorage.setItem('selectedPlan', JSON.stringify(planInfo))
                              
                              // Check login status
                              const loginStatus = localStorage.getItem("isLoggedIn") === "true"
                              
                              if (loginStatus) {
                                // User is logged in → go to payment window
                                router.push(`/pathway?window=payment&planId=${plan.id}`)
                              } else {
                                // User is not logged in → go to mobile-auth window first
                                localStorage.setItem('redirectAfterLogin', JSON.stringify({
                                  window: 'payment',
                                  planId: plan.id
                                }))
                                router.push('/pathway?window=mobile-auth')
                              }
                            }}
                          >
                            {t('pricing.getStart')}
                            <ArrowLeft className="mr-2 w-3 h-3" />
                          </Button>
                        </div>
                        <div className="space-y-2.5">
                          {plan.features.slice(0, 5).map((feature, idx) => (
                            <div key={idx} className="flex items-start gap-1.5">
                              {typeof feature.value === 'boolean' ? (
                                feature.value ? (
                                  <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                ) : (
                                  <X className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
                                )
                              ) : (
                                <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                              )}
                              <div className="flex-1 flex flex-col">
                                <span className="text-xs text-gray-700">{feature.name}</span>
                                {typeof feature.value === 'string' && (
                                  <span className="text-xs font-semibold text-[#08a463]">
                                    {feature.value}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}
            </>
          )}

          {/* Feature Comparison Table */}
          {pricingPlans.length > 0 && (
            <div className="bg-white rounded-3xl border-2 border-gray-200 overflow-hidden shadow-xl">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-right p-4 font-semibold text-gray-900">{t('pricing.features')}</th>
                      {pricingPlans.slice(0, 4).map((plan) => (
                        <th key={plan.id} className="text-center p-4 font-semibold text-gray-900">
                          {plan.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pricingPlans[0]?.features.map((feature, idx) => (
                      <tr key={idx} className="border-t border-gray-200 hover:bg-gray-50">
                        <td className="p-4">
                          <div className="flex items-center gap-2 group relative">
                            <span className="text-xs text-gray-700">{feature.name}</span>
                            <div className="relative">
                              <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                                {feature.tooltip}
                              </div>
                            </div>
                          </div>
                        </td>
                        {pricingPlans.slice(0, 4).map((plan) => {
                          const planFeature = plan.features[idx]
                          return (
                            <td key={plan.id} className="text-center p-3">
                              {typeof planFeature?.value === 'boolean' ? (
                                planFeature.value ? (
                                  <Check className="w-4 h-4 text-green-500 mx-auto" />
                                ) : (
                                  <X className="w-4 h-4 text-gray-300 mx-auto" />
                                )
                              ) : (
                                <span className="text-xs font-semibold text-[#01ADEF]">
                                  {planFeature?.value || '-'}
                                </span>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm mb-3">
              <CheckCircle2 className="w-4 h-4 text-green-500 inline ml-1" />
              {t('pricing.moneyBackGuarantee')}
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-6 gap-8 mb-8">
            {/* AI Association Membership - Left Side */}
            <div className="flex md:justify-start justify-center items-start">
              <img 
                src='/images/عضو-انجمن-ملی-هوش مصنوعی-ایران.png' 
                alt='عضو انجمن ملی هوش مصنوعی ایران' 
                title='عضو انجمن ملی هوش مصنوعی ایران'
                style={{cursor:'pointer'}} 
                width={180}
                height={180}
                className="object-contain"
              />
            </div>
            {/* Trust Seal - Government Certificate */}
            <div className="flex md:justify-center justify-center items-start">
              <a referrerPolicy='origin' target='_blank' href='https://trustseal.enamad.ir/?id=687662&Code=R8yDKM1WZfJYZzJcNFvZCi1IUg2yuBLd'>
                <img 
                  referrerPolicy='origin' 
                  src='https://trustseal.enamad.ir/logo.aspx?id=687662&Code=R8yDKM1WZfJYZzJcNFvZCi1IUg2yuBLd' 
                  alt='نماد اعتماد الکترونیکی enamad' 
                  style={{cursor:'pointer'}} 
                  width={85}
                  height={85}
                  className="object-contain"
                />
              </a>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Image src="/dist2/robot-icon.png" alt="آتیرام" width={40} height={40} className="object-contain" />
                <span className="text-xl font-bold text-white">آتیرام</span>
              </div>
              <p className="text-sm">
                {t('footer.tagline')}
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footer.product')}</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition">{t('footer.productFeatures')}</a></li>
                <li><a href="#" className="hover:text-white transition">{t('footer.productPricing')}</a></li>
                <li><a href="#" className="hover:text-white transition">{t('footer.productDocs')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footer.company')}</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="https://www.atiradco.ir" className="hover:text-white transition">{t('footer.aboutUs')}</a></li>
                <li><a href="https://www.atiradco.ir" className="hover:text-white transition">{t('footer.contactUs')}</a></li>
                <li><a href="/blog" className="hover:text-white transition">{t('footer.blog')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footer.support')}</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition">{t('footer.helpCenter')}</a></li>
                <li><a href="tel:02177645193" className="hover:text-white transition">021-77645193</a></li>
                <li><a href="tel:02177645194" className="hover:text-white transition">021-77645194</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          </div>
        </div>
      </footer>
      </div>
    </div>
  )
}